import Header from "./Header";
import Footer from "./Footer";
import ChatBot from "./ChatBot";
import "../styles/LegalInsights.css";
import { useState } from "react";
import {
    FaHome,
    FaUsers,
    FaBriefcase,
    FaBalanceScale,
    FaFileContract,
    FaShoppingCart,
    FaCalendarAlt,
    FaChartLine,
    FaArrowRight,
    FaChevronDown,
    FaChevronUp,
    FaClock
} from "react-icons/fa";
import {
    MdUpdate,
    MdTipsAndUpdates,
    MdWarning
} from "react-icons/md";

export default function LegalInsights() {
    // State to track expanded/collapsed state for each card
    const [expandedCards, setExpandedCards] = useState({});

    const toggleCard = (cardId) => {
        setExpandedCards(prev => ({
            ...prev,
            [cardId]: !prev[cardId]
        }));
    };

    const legalInsightsData = [
        {
            id: 1,
            icon: <FaHome />,
            title: "Property & Real Estate Law",
            summary: "Essential information about renting, buying, and selling property in the UK.",
            detailedContent: `
        <div class="insight-content-section">
          <h4>Renting Rights in Detail:</h4>
          <p><strong>Deposit Protection:</strong> Landlords must place your deposit in a government-approved scheme within 30 days. You should receive prescribed information about the scheme. Failure to comply can result in the landlord paying you 1-3 times the deposit amount.</p>
        </div>
        
        <div class="insight-content-section">
          <h4>Key Tenancy Rights:</h4>
          <ul>
            <li><strong>Right to Quiet Enjoyment:</strong> Landlords cannot harass you or enter without 24 hours written notice (except emergencies)</li>
            <li><strong>Repairs Responsibility:</strong> Landlords must fix structural issues, heating, hot water, gas, and electrical safety</li>
            <li><strong>Eviction Process:</strong> Section 21 'no-fault' evictions require 2 months notice. Section 8 evictions require specific grounds like rent arrears</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>Buying Property Process:</h4>
          <ol>
            <li><strong>Offer & Acceptance:</strong> Verbal agreement, then solicitor instructed</li>
            <li><strong>Conveyancing:</strong> Searches (local authority, environmental, water), review contracts</li>
            <li><strong>Mortgage:</strong> Formal offer, valuation survey</li>
            <li><strong>Exchange:</strong> Contracts signed, deposit paid (usually 10%)</li>
            <li><strong>Completion:</strong> Balance paid, keys collected, registration with Land Registry</li>
          </ol>
        </div>
        
        <div class="insight-content-section">
          <p><strong>Stamp Duty Land Tax (SDLT):</strong> First-time buyers pay 0% on first £425,000. Standard rates: 0% up to £250,000, 5% on £250,001-£925,000, 10% on £925,001-£1.5M, 12% above £1.5M.</p>
        </div>
      `
        },
        {
            id: 2,
            icon: <FaUsers />,
            title: "Family Law Essentials",
            summary: "Understanding divorce, child arrangements, and financial settlements.",
            detailedContent: `
        <div class="insight-content-section">
          <h4>No-Fault Divorce Process:</h4>
          <p>Since April 2022, you no longer need to prove fault. The sole ground for divorce is 'irretrievable breakdown' of marriage.</p>
        </div>
        
        <div class="insight-content-section">
          <ol>
            <li><strong>Apply Online:</strong> Submit divorce application to HMCTS (£593 fee, may qualify for help)</li>
            <li><strong>20-Week Reflection Period:</strong> Mandatory cooling-off period</li>
            <li><strong>Conditional Order:</strong> Court confirms marriage can be dissolved (6 weeks after application)</li>
            <li><strong>Final Order:</strong> Marriage legally ends (6 weeks after conditional order)</li>
          </ol>
        </div>
        
        <div class="insight-content-section">
          <h4>Financial Settlements:</h4>
          <p>Courts consider 'needs, compensation, and sharing' principles. Factors include:</p>
          <ul>
            <li>Income and earning capacity of each party</li>
            <li>Financial needs and obligations</li>
            <li>Standard of living during marriage</li>
            <li>Age and duration of marriage</li>
            <li>Contributions (financial and non-financial)</li>
            <li>Conduct (only in exceptional circumstances)</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>Child Arrangements:</h4>
          <p>Courts prioritise child's welfare (Children Act 1989). Key principles:</p>
          <ul>
            <li>Delay is harmful - cases should be resolved quickly</li>
            <li>No order unless better for the child</li>
            <li>Both parents usually continue involvement</li>
            <li>Consider child's wishes according to age/understanding</li>
          </ul>
        </div>
      `
        },
        {
            id: 3,
            icon: <FaBriefcase />,
            title: "Employment Rights",
            summary: "Your rights at work, from unfair dismissal to discrimination protection.",
            detailedContent: `
        <div class="insight-content-section">
          <h4>Unfair Dismissal Protection:</h4>
          <p><strong>Qualifying Period:</strong> 2 years continuous service required (except for automatically unfair reasons).</p>
        </div>
        
        <div class="insight-content-section">
          <h4>Automatically Unfair Reasons (no service requirement):</h4>
          <ul>
            <li>Pregnancy, maternity, paternity, adoption leave</li>
            <li>Trade union membership or activities</li>
            <li>Whistleblowing (Public Interest Disclosure)</li>
            <li>Health and safety activities</li>
            <li>Working time regulations (rest breaks, holiday pay)</li>
            <li>National Minimum Wage enforcement</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>Discrimination Law:</h4>
          <p><strong>Protected Characteristics (Equality Act 2010):</strong></p>
          <ul>
            <li><strong>Age:</strong> Protection for all ages, not just older workers</li>
            <li><strong>Disability:</strong> Employers must make 'reasonable adjustments'</li>
            <li><strong>Gender Reassignment:</strong> Protection from day one of proposing transition</li>
            <li><strong>Marriage/Civil Partnership:</strong> But not single status</li>
            <li><strong>Pregnancy/Maternity:</strong> Enhanced protection</li>
            <li><strong>Race:</strong> Includes colour, nationality, ethnic/national origins</li>
            <li><strong>Religion/Belief:</strong> Includes non-religious beliefs</li>
            <li><strong>Sex:</strong> Men and women equally protected</li>
            <li><strong>Sexual Orientation:</strong> Straight, gay, bisexual</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>ACAS Early Conciliation:</h4>
          <p>Mandatory before tribunal claim. ACAS contacts both parties for voluntary settlement (up to 6 weeks). Certificate issued if unsuccessful.</p>
        </div>
      `
        },
        {
            id: 4,
            icon: <FaBalanceScale />,
            title: "Criminal Law Basics",
            summary: "Understanding police procedures, court processes, and your fundamental rights.",
            detailedContent: `
        <div class="insight-content-section">
          <h4>Police Powers & Your Rights:</h4>
          
          <h5>When Arrested:</h5>
          <ul>
            <li><strong>Right to Silence:</strong> "You do not have to say anything, but it may harm your defence if you do not mention when questioned something which you later rely on in court. Anything you do say may be given in evidence."</li>
            <li><strong>Right to Legal Advice:</strong> Free 24/7 duty solicitor available</li>
            <li><strong>Right to Inform Someone:</strong> Can have someone told of arrest</li>
            <li><strong>Right to Medical Help:</strong> If required</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h5>Custody Time Limits:</h5>
          <ul>
            <li><strong>Without Charge:</strong> Generally maximum 24 hours (extendable to 36 for serious offences)</li>
            <li><strong>With Superintendent Approval:</strong> Up to 96 hours for indictable offences</li>
            <li><strong>With Court Permission:</strong> Up to 96 hours total</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>Court System Structure:</h4>
          
          <h5>Magistrates' Court:</h5>
          <ul>
            <li>95% of criminal cases start here</li>
            <li>Maximum sentence: 6 months prison per offence (12 months total)</li>
            <li>Unlimited fines for some offences</li>
            <li>3 magistrates or 1 district judge</li>
            <li>No jury</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h5>Crown Court:</h5>
          <ul>
            <li>For serious offences (indictable only)</li>
            <li>Appeals from magistrates' court</li>
            <li>Judge and jury (12 members)</li>
            <li>Unlimited sentencing powers</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>Sentencing Guidelines:</h4>
          <p>Courts follow structured approach:</p>
          <ol>
            <li>Determine offence category (culpability/harm)</li>
            <li>Starting point and category range</li>
            <li>Aggravating/mitigating factors</li>
            <li>Consider guilty plea reduction (up to 1/3)</li>
            <li>Totality principle (overall sentence)</li>
          </ol>
        </div>
      `
        },
        {
            id: 5,
            icon: <FaFileContract />,
            title: "Wills & Probate",
            summary: "Creating valid wills and navigating the probate process.",
            detailedContent: `
        <div class="insight-content-section">
          <h4>Creating a Valid Will (Wills Act 1837):</h4>
          
          <p><strong>Essential Requirements:</strong></p>
          <ol>
            <li><strong>Age 18+</strong> (except armed forces on active service)</li>
            <li><strong>Mental Capacity:</strong> Understands nature of making will, extent of property, and claims of potential beneficiaries</li>
            <li><strong>In Writing:</strong> Can be handwritten, typed, printed</li>
            <li><strong>Signature:</strong> Testator must sign or acknowledge signature in presence of 2 witnesses</li>
            <li><strong>Witnesses:</strong> 2 independent witnesses present together who also sign</li>
          </ol>
        </div>
        
        <div class="insight-content-section">
          <p><strong>Witness Restrictions:</strong> Witnesses or their spouses/civil partners cannot benefit from will.</p>
        </div>
        
        <div class="insight-content-section">
          <h4>Intestacy Rules (If No Valid Will):</h4>
          
          <p><strong>England & Wales Rules:</strong></p>
          <ul>
            <li><strong>Spouse/Civil Partner Only:</strong> Inherits all estate up to £322,000 plus personal possessions. Balance split 50% to spouse, 50% to children</li>
            <li><strong>Spouse + Children:</strong> Spouse gets £322,000 + personal possessions + 50% of remainder. Children share 50% of remainder at 18</li>
            <li><strong>No Spouse, With Children:</strong> Children inherit everything equally at 18</li>
            <li><strong>No Spouse/Children:</strong> Parents → siblings → nieces/nephews → grandparents → aunts/uncles → cousins → Crown</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>Probate Process:</h4>
          
          <ol>
            <li><strong>Register Death:</strong> Get death certificate (within 5 days in England/Wales)</li>
            <li><strong>Find Will:</strong> Locate original will if exists</li>
            <li><strong>Value Estate:</strong> List all assets and debts at date of death</li>
            <li><strong>Check Inheritance Tax:</strong> Nil-rate band £325,000. Additional £175,000 residence nil-rate band if leaving home to descendants</li>
            <li><strong>Apply for Grant:</strong> Probate (with will) or Letters of Administration (no will)</li>
            <li><strong>Collect Assets:</strong> Use grant to access accounts, sell property</li>
            <li><strong>Pay Debts/Tax:</strong> Settle outstanding liabilities</li>
            <li><strong>Distribute Estate:</strong> Transfer remaining assets to beneficiaries</li>
          </ol>
        </div>
        
        <div class="insight-content-section">
          <p><strong>Timeframe:</strong> Typically 6-12 months for straightforward estates. Complex estates can take 2+ years.</p>
        </div>
      `
        },
        {
            id: 6,
            icon: <FaShoppingCart />,
            title: "Consumer Rights",
            summary: "Your rights when buying goods, services, and digital content.",
            detailedContent: `
        <div class="insight-content-section">
          <h4>Consumer Rights Act 2015 - Key Protections:</h4>
          
          <h5>Goods Must Be:</h5>
          <ol>
            <li><strong>Satisfactory Quality:</strong> Fit for purpose, free from defects, safe, durable</li>
            <li><strong>As Described:</strong> Match description, samples, models shown</li>
            <li><strong>Fit for Particular Purpose:</strong> Any specific purpose made known to seller</li>
          </ol>
        </div>
        
        <div class="insight-content-section">
          <h4>Your Rights Timeline:</h4>
          
          <ul>
            <li><strong>0-30 Days:</strong> Full refund if faulty (right to reject)</li>
            <li><strong>30 Days - 6 Months:</strong> One repair/replacement attempted, then refund (deduction for use)</li>
            <li><strong>After 6 Months:</strong> Must prove fault existed at time of sale (usually needs expert report)</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>Digital Content Rights:</h4>
          
          <p>Digital content includes apps, games, music, films, e-books. Must be:</p>
          <ul>
            <li>Satisfactory quality</li>
            <li>Fit for purpose</li>
            <li>As described</li>
            <li>Receive updates as described</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <p><strong>Remedy:</strong> Repair/replacement or price reduction. If damages device, right to repair/compensation.</p>
        </div>
        
        <div class="insight-content-section">
          <h4>Services Contracts:</h4>
          
          <p>Services must be performed with reasonable care and skill, within reasonable time, at reasonable price if not agreed.</p>
        </div>
        
        <div class="insight-content-section">
          <h5>Cancellation Rights:</h5>
          <ul>
            <li><strong>Distance/Off-Premises Contracts:</strong> 14-day cooling-off period (exceptions: personalised, perishable, sealed audio/video/software once opened)</li>
            <li><strong>Doorstep Selling:</strong> 14 days to cancel contracts over £42 made in your home/workplace</li>
            <li><strong>Timeshare/Credit Agreements:</strong> 14 days cooling-off</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <h4>Unfair Contract Terms:</h4>
          
          <p>Consumer Rights Act 2015 prohibits unfair terms. Terms are unfair if:</p>
          <ul>
            <li>Causes significant imbalance in parties' rights</li>
            <li>Contrary to good faith</li>
            <li>Detrimental to consumer</li>
          </ul>
        </div>
        
        <div class="insight-content-section">
          <p><strong>Examples of Unfair Terms:</strong></p>
          <ul>
            <li>Excluding liability for death/injury from negligence</li>
            <li>Allowing business to change terms unilaterally</li>
            <li>Excessive cancellation charges</li>
            <li>Unfair retention of deposits</li>
          </ul>
        </div>
      `
        }
    ];

    // Recent updates data
    const recentUpdates = [
        {
            date: "March 2024",
            title: "Online Safety Act 2023 Implementation",
            content: "Social media platforms now have enhanced duties to protect users from harmful content, with Ofcom as regulator. Key requirements include risk assessments, age verification measures, and transparency reporting."
        },
        {
            date: "January 2024",
            title: "Building Safety Act Changes",
            content: "Leaseholders in medium-rise buildings (11-18 metres) now protected from cladding remediation costs. New building safety regime established with Accountable Persons responsible for building safety risks."
        },
        {
            date: "December 2023",
            title: "Economic Crime Act Updates",
            content: "Strengthened powers to combat money laundering and fraud. Companies House reforms increase verification requirements. New corporate criminal offence of failure to prevent fraud for large organisations."
        }
    ];

    // Practical Tips data
    const practicalTips = [
        {
            icon: <FaClock />,
            title: "Act Quickly on Legal Issues",
            content: "Many legal claims have strict time limits (limitation periods). Personal injury claims usually have 3 years, contract disputes 6 years. Delaying can mean losing your right to claim."
        },
        {
            icon: <FaFileContract />,
            title: "Always Get It in Writing",
            content: "Verbal agreements are hard to prove. Get written contracts for any significant agreement. Keep records of all communications, receipts, and documents related to your case."
        },
        {
            icon: <FaBalanceScale />,
            title: "Know When to Seek Professional Help",
            content: "While online resources are helpful, complex legal matters require professional advice. Look for solicitors regulated by the SRA with expertise in your specific area of need."
        },
        {
            icon: <FaHome />,
            title: "Document Everything",
            content: "Maintain a detailed record of events, dates, conversations, and expenses. This documentation can be crucial evidence if you need to pursue legal action."
        }
    ];

    return (
        <div className="legal-insights-container">
            <Header />

            {/* HERO SECTION */}
            <section className="legal-hero-section">
                <div className="hero-content-wrapper">
                    <h1 className="hero-main-title">
                        Legal Insights & <span className="hero-highlight">Resources</span>
                    </h1>
                    <p className="hero-description">
                        Clear, practical legal information to help you navigate complex situations.
                        Understand your rights, learn about processes, and make informed decisions.
                    </p>
                </div>
            </section>

            {/* MAIN CONTENT */}
            <main className="legal-main-content">
                <div className="content-layout-wrapper">

                    {/* INSIGHTS GRID */}
                    <div className="insights-grid-section">
                        <div className="section-header">
                            <div className="header-icon-wrapper">
                                <FaChartLine className="section-icon" />
                            </div>
                            <h2 className="section-title">Legal Practice Areas</h2>
                            <p className="section-subtitle">Comprehensive guides on key legal topics</p>
                        </div>

                        <div className="insights-grid">
                            {legalInsightsData.map(insight => (
                                <div
                                    key={insight.id}
                                    className={`insight-card-item ${expandedCards[insight.id] ? 'expanded' : ''}`}
                                >
                                    <div className="card-header">
                                        <div className="card-icon-wrapper">
                                            {insight.icon}
                                        </div>
                                        <h3 className="card-title">{insight.title}</h3>
                                    </div>

                                    <p className="card-summary">{insight.summary}</p>

                                    {expandedCards[insight.id] && (
                                        <div
                                            className="card-detailed-content"
                                            dangerouslySetInnerHTML={{ __html: insight.detailedContent }}
                                        />
                                    )}

                                    <button
                                        className="card-toggle-btn"
                                        onClick={() => toggleCard(insight.id)}
                                    >
                                        <span className="btn-text">
                                            {expandedCards[insight.id] ? 'Show Less' : 'Learn More'}
                                        </span>
                                        {expandedCards[insight.id] ? <FaChevronUp /> : <FaChevronDown />}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* UPDATES SIDEBAR */}
                    <div className="sidebar-section">

                        {/* LEGAL UPDATES */}
                        <div className="updates-card">
                            <div className="updates-header">
                                <MdUpdate className="updates-icon" />
                                <h3 className="updates-title">Recent Legal Updates</h3>
                            </div>

                            <div className="updates-list">
                                {recentUpdates.map((update, index) => (
                                    <div key={index} className="update-item-card">
                                        <div className="update-meta">
                                            <FaCalendarAlt className="date-icon" />
                                            <span className="update-date">{update.date}</span>
                                        </div>
                                        <h4 className="update-heading">{update.title}</h4>
                                        <p className="update-content">{update.content}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </main>


            {/* CTA SECTION */}
            <section className="legal-cta-section">
                <div className="cta-content-wrapper">
                    <div className="cta-text-content">
                        <h2 className="cta-title">Need Specific Legal Guidance?</h2>
                        <p className="cta-description">
                            Our verified solicitors can provide advice tailored to your unique situation.
                            Get matched with a specialist in your area of need.
                        </p>
                    </div>

                    <div className="cta-buttons-group">
                        <a href="/practice-areas" className="cta-primary-btn">
                            <span>Browse Practice Areas</span>
                            <FaArrowRight className="btn-icon" />
                        </a>
                        <a href="/search" className="cta-secondary-btn">
                            <span>Find a Solicitor Now</span>
                        </a>
                    </div>

                    <div className="cta-footer-note">
                        <FaClock className="note-icon" />
                        <span>Most solicitors offer a free initial consultation (15-30 minutes) to assess your case.</span>
                    </div>
                </div>
            </section>

            <ChatBot />
            <Footer />
        </div>
    );
}